import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import style from './Image.module.scss';

const viewPortOffset = 300;

export interface ImageProps {
    unCompressed: string;
    unCompressed2x?: string;
    webp?: string;
    webp2x?: string;
    className?: string;
    'data-testid'?: string;
    alt: string;
    lazy?: boolean;
}

const Image = React.forwardRef<HTMLPictureElement, ImageProps>(
    ({ unCompressed, unCompressed2x = '', webp = '', webp2x = '', className, alt, lazy = true, ...props }, refPic) => {
        const ref = React.createRef<HTMLImageElement>();
        const [normalImg, setNormalImg] = useState('');
        const [imgSrcSet, setImgSrcSet] = useState('');
        const [webpSrcSet, setWebpSrcSet] = useState('');

        const isAlmostOnViewport = (el: HTMLElement) => {
            const rect = el.getBoundingClientRect();
            return rect.top <= window.innerHeight + viewPortOffset;
        };

        const getSrcSet = (image: String, image2x = '') => {
            return `${image}${image2x ? `, ${image2x} 2x` : ''}`;
        };

        const handleScreenChanges = () => {
            if (!ref.current) return;
            if (isAlmostOnViewport(ref.current)) {
                setNormalImg(unCompressed);
                setImgSrcSet(getSrcSet(unCompressed, unCompressed2x));
                if (webp) {
                    setWebpSrcSet(getSrcSet(webp, webp2x));
                }

                window.removeEventListener('scroll', handleScreenChanges);
                window.removeEventListener('resize', handleScreenChanges);
            }
        };
        useEffect(() => {
            handleScreenChanges();
            window.addEventListener('scroll', handleScreenChanges);
            window.addEventListener('resize', handleScreenChanges);
            return () => {
                window.removeEventListener('scroll', handleScreenChanges);
                window.removeEventListener('resize', handleScreenChanges);
            };
        });

        return (
            <>
                {lazy ? (
                    <picture
                        ref={refPic}
                        className={classnames(style.image, className)}
                        data-testid={props['data-testid']}
                    >
                        {webp && <source srcSet={webpSrcSet} type="image/webp" />}
                        <source srcSet={imgSrcSet} />
                        <img src={normalImg} data-src={unCompressed} ref={ref} alt={alt} />
                    </picture>
                ) : (
                    <picture className={classnames(style.image, className)} data-testid={props['data-testid']}>
                        {webp && <source srcSet={getSrcSet(webp, webp2x)} type="image/webp" />}
                        <source srcSet={getSrcSet(unCompressed, unCompressed2x)} />
                        <img src={unCompressed} alt={alt} />
                    </picture>
                )}
            </>
        );
    },
);

export default Image;
